import { $setBlocksType } from '@lexical/selection';
import { mergeRegister } from '@lexical/utils';
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_EDITOR,
  createCommand,
  LexicalEditor,
  ElementNode,
  KEY_ENTER_COMMAND,
  INSERT_PARAGRAPH_COMMAND,
  COMMAND_PRIORITY_HIGH,
  SELECTION_CHANGE_COMMAND,
  $isTextNode,
} from 'lexical';

import { HeadingTagType } from 'const';
import { $createCustomHeadingNode, $isCustomHeadingNode, CustomHeadingNode } from '../../nodes/CustomHeadingNode';
import { $isCustomListItemNode } from '../../nodes/CustomListItemNode';
import { $isCustomParagraphNode } from '../../nodes/CustomParagraphNode';
import { $getPrevNonEmptyNode } from './utils';

export const HEADING_TAG_COMMAND = createCommand<HeadingTagType | undefined>();

type Props = {
  editor: LexicalEditor;
};

export function registerCommands({ editor }: Props): () => void {
  return mergeRegister(

    editor.registerCommand(
      HEADING_TAG_COMMAND,
      (value) => {
        editor.update(() => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection) || !value) {
            return;
          }
          $setBlocksType(selection, () => $createCustomHeadingNode(value), (prevNodeSrc: ElementNode, newNodeDest: CustomHeadingNode) => {
            if ($isCustomParagraphNode(prevNodeSrc) || $isCustomListItemNode(prevNodeSrc) || $isCustomHeadingNode(prevNodeSrc)) {
              newNodeDest.setLineHeight(prevNodeSrc.getLineHeight());
              newNodeDest.setFontSize(prevNodeSrc.getFontSize());
              newNodeDest.setFormat(prevNodeSrc.getFormatType());
            }
          });
        });

        return true;

      },
      COMMAND_PRIORITY_EDITOR,
    ),

    // Intercept KEY_ENTER_COMMAND for the heading tag plugin
    // so that after creating a new paragraph we can set the appropriate heading tag.
    editor.registerCommand<KeyboardEvent | null>(
      KEY_ENTER_COMMAND,
      (event) => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {

          return false;
        }
        if (event !== null) {
          event.preventDefault();
        }
        const topLevelNode = selection.anchor.getNode().getTopLevelElement();
        const isHeadingNode = $isCustomHeadingNode(topLevelNode);
        if (!isHeadingNode) {
          return false;
        }
        const tag = topLevelNode.getTag();
        const result = editor.dispatchCommand(INSERT_PARAGRAPH_COMMAND, undefined);
        $setBlocksType(selection, () => $createCustomHeadingNode(tag, topLevelNode.getLineHeight(), topLevelNode.getFontSize()));

        return result;
      },
      COMMAND_PRIORITY_HIGH,
    ),

    editor.registerCommand<KeyboardEvent | null>(
      SELECTION_CHANGE_COMMAND,
      () => {
        if (!editor.isEditable()) {
          return true;
        }
        const selection = $getSelection();
        // When Enter is pressed a second time,
        // Lexical resets the styles on the current selection.
        // Therefore we manually re-apply previous node font styles here.
        if ($isRangeSelection(selection) && !selection.style) {
          const anchor = selection.anchor.getNode();
          const prevNode = $getPrevNonEmptyNode(anchor);
          const prevNodeStyle = $isTextNode(prevNode) ? prevNode.getStyle() : '';
          if (prevNodeStyle) {
            selection.setStyle(prevNodeStyle);
          }
        }

        return false;

      },
      COMMAND_PRIORITY_HIGH,
    ),
  );
}
